import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, ChevronLeft, Check, Search, CheckCircle2, Ticket, PartyPopper, Loader2, Scissors, Star } from 'lucide-react';

const API_BASE_URL = 'https://api-admin.randevupp.com';

const TURKEY_CITIES = [
  "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Amasya", "Ankara", "Antalya", "Artvin", "Aydın", "Balıkesir", "Bilecik", "Bingöl", "Bitlis", "Bolu", "Burdur", "Bursa", "Çanakkale", "Çankırı", "Çorum", "Denizli", "Diyarbakır", "Edirne", "Elazığ", "Erzincan", "Erzurum", "Eskişehir", "Gaziantep", "Giresun", "Gümüşhane", "Hakkari", "Hatay", "Isparta", "Mersin", "İstanbul", "İzmir", "Kars", "Kastamonu", "Kayseri", "Kırklareli", "Kırşehir", "Kocaeli", "Konya", "Kütahya", "Malatya", "Manisa", "Kahramanmaraş", "Mardin", "Muğla", "Muş", "Nevşehir", "Niğde", "Ordu", "Rize", "Sakarya", "Samsun", "Siirt", "Sinop", "Sivas", "Tekirdağ", "Tokat", "Trabzon", "Tunceli", "Şanlıurfa", "Uşak", "Van", "Yozgat", "Zonguldak", "Aksaray", "Bayburt", "Karaman", "Kırıkkale", "Batman", "Şırnak", "Bartın", "Ardahan", "Iğdır", "Yalova", "Karabük", "Kilis", "Osmaniye", "Düzce"
].sort((a, b) => a.localeCompare(b, 'tr'));

const inputClasses = "w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-gray-900 dark:text-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 outline-none transition-all placeholder-gray-400 dark:placeholder-gray-500";

const SearchableDropdown = ({ value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCities = TURKEY_CITIES.filter(city => 
    city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    city.toLocaleLowerCase('tr').includes(searchTerm.toLocaleLowerCase('tr'))
  );

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className={`${inputClasses} cursor-pointer flex justify-between items-center ${isOpen ? 'border-primary-500 ring-4 ring-primary-500/20' : ''}`}
      >
        <span className={value ? "text-gray-900 dark:text-white font-medium" : "text-gray-400 dark:text-gray-500"}>
          {value || placeholder}
        </span>
        <ChevronRight className={`transition-transform duration-300 ${isOpen ? 'rotate-90' : ''}`} size={20} />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden"
          >
            <div className="p-3 border-b-2 border-gray-100 dark:border-gray-700 flex items-center gap-2">
              <Search size={18} className="text-gray-400" />
              <input 
                type="text" 
                autoFocus
                placeholder="Şehir Ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
            <div className="max-h-52 overflow-y-auto custom-scrollbar">
              {filteredCities.length > 0 ? (
                filteredCities.map(city => (
                  <div 
                    key={city}
                    onClick={() => {
                      onChange(city);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className={`px-4 py-3 cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/30 flex items-center justify-between transition-colors ${value === city ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-600 font-bold' : 'text-gray-700 dark:text-gray-300 font-medium'}`}
                  >
                    {city}
                    {value === city && <Check size={18} className="text-primary-500" />}
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500 font-medium">Şehir bulunamadı</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const HairfestApplication = () => {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const formTopRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_code: '+90',
    phone: '',
    city: '',
    district: '',
    business_name: '',
    requested_domain: '',
    interest_level: 5,
    extra_notes: '',
    package_id: null,
    promo_code: ''
  });

  // Promo State
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [validatingPromo, setValidatingPromo] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setIsLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/public/packages`);
        const data = await res.json();
        if (data.success && data.data) {
          setPackages(data.data.filter(p => p.is_active));
        }
      } catch (error) {
        console.error("Paketler yüklenirken hata oluştu:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const scrollToTop = () => {
    if (formTopRef.current) {
      setTimeout(() => {
        formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.first_name || !formData.last_name || !formData.phone) return alert("Lütfen tüm zorunlu alanları doldurun.");
    }
    if (step === 2) {
      if (!formData.business_name || !formData.city || !formData.district) return alert("Lütfen işletme bilgilerinizi eksiksiz doldurun.");
    }
    setDirection(1);
    setStep(prev => prev + 1);
    scrollToTop();
  };

  const handleBack = () => {
    setDirection(-1);
    setStep(prev => prev - 1);
    scrollToTop();
  };

  const validatePromo = async () => {
    if (!promoInput.trim()) return;
    try {
      setValidatingPromo(true);
      setPromoError('');
      
      const res = await fetch(`${API_BASE_URL}/api/public/promo/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoInput })
      });
      const data = await res.json();
      
      if (data.success) {
        setAppliedPromo(data.data);
        setFormData(prev => ({ ...prev, promo_code: data.data.code }));
      } else {
        setPromoError(data.message || "Geçersiz promosyon kodu.");
        setAppliedPromo(null);
        setFormData(prev => ({ ...prev, promo_code: '' }));
      }
    } catch (error) {
      setPromoError("Bağlantı hatası oluştu.");
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.package_id) return alert("Lütfen bir paket seçin.");

    try {
      setIsSubmitting(true);
      
      const res = await fetch(`${API_BASE_URL}/api/public/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      
      if (data.success || res.status === 201) {
        setIsSuccess(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        alert(data.message || "Başvuru gönderilirken bir hata oluştu.");
      }
    } catch (error) {
      alert("Sistemle bağlantı kurulamadı. Lütfen daha sonra tekrar deneyin.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPackage = packages.find(p => p.id === formData.package_id);
  let basePrice = selectedPackage ? parseFloat(selectedPackage.price) : 0;
  let taxRate = selectedPackage ? parseFloat(selectedPackage.tax_rate || 20) : 20;
  let discountAmount = appliedPromo && basePrice > 0 ? basePrice * (appliedPromo.discount_percentage / 100) : 0;
  let netPrice = basePrice - discountAmount;
  let taxAmount = netPrice * (taxRate / 100);
  let finalPrice = netPrice + taxAmount;

  const variants = {
    enter: (direction) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
    center: { zIndex: 1, x: 0, opacity: 1 },
    exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 50 : -50, opacity: 0 })
  };

  if (isSuccess) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-20 px-4">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-md w-full glass-panel p-10 rounded-[3rem] text-center border-2 border-green-500/20">
          <div className="w-24 h-24 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-green-500/30">
            <CheckCircle2 size={48} />
          </div>
          <h2 className="text-3xl font-extrabold mb-4 text-gray-900 dark:text-white">Başvurunuz Alındı!</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 font-medium leading-relaxed">
            Fethiye Hairfest indirimli başvurunuz başarıyla ulaştı. Ekibimiz <strong>{formData.phone}</strong> numaralı telefondan sizinle hemen iletişime geçecektir.
          </p>
          <a href="/" className="btn-primary w-full justify-center inline-flex py-4 rounded-xl text-lg">Ana Sayfaya Dön</a>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Hairfest Özel Başvurusu | RandevUPP 2.0</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-primary-50 to-amber-50 dark:from-gray-900 dark:to-gray-800 pt-32 pb-24 relative overflow-hidden border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 mb-8 text-primary-600 dark:text-primary-400 font-bold tracking-wide">
            <PartyPopper size={20} className="text-amber-500" /> Fethiye Hairfest '26 Özel Başvurusu
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 text-gray-900 dark:text-white tracking-tight">
            İşletmenizi Geleceğe Taşıyın
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto font-medium">
            RandevUPP 2.0 ile kendi internet sitenizde sınırsız yönetime başlayın. Formu doldurun, festival özel indirimlerini anında başvuru özetinizde görün.
          </motion.p>
        </div>
      </div>

      <div ref={formTopRef} className="max-w-6xl mx-auto px-4 -mt-10 mb-24 relative z-20">
        <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-2xl border border-gray-200 dark:border-gray-800 p-6 md:p-10 flex flex-col md:flex-row gap-10">
          
          {/* Form Side */}
          <div className="w-full md:w-[60%] overflow-hidden relative">
            {/* Steps Indicator */}
            <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-gray-100 dark:border-gray-800">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex flex-col items-center gap-2 relative z-10">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-500 border-4 ${step === i ? 'bg-primary-500 text-white border-primary-200 dark:border-primary-900/50 shadow-xl scale-110' : step > i ? 'bg-primary-100 text-primary-600 border-primary-100 dark:bg-primary-900 dark:text-primary-400 dark:border-primary-900' : 'bg-gray-100 text-gray-400 border-white dark:bg-gray-800 dark:border-gray-900'}`}>
                    {step > i ? <Check size={20} className="stroke-[3]" /> : i}
                  </div>
                  <span className={`text-sm font-bold hidden sm:block transition-colors ${step === i ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`}>
                    {i === 1 ? 'Kişisel' : i === 2 ? 'İşletme' : 'Tercihler'}
                  </span>
                </div>
              ))}
              <div className="absolute top-6 left-12 right-12 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full -z-0">
                <div className="h-full bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${(step - 1) * 50}%` }}></div>
              </div>
            </div>

            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={step}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="w-full"
              >
                {/* STEP 1: Kişisel Bilgiler */}
                {step === 1 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-extrabold mb-6 text-gray-900 dark:text-white">Kişisel Bilgileriniz</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Adınız *</label>
                        <input type="text" className={inputClasses} value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} placeholder="Örn: Ahmet" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Soyadınız *</label>
                        <input type="text" className={inputClasses} value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} placeholder="Örn: Yılmaz" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Telefon Numaranız *</label>
                      <div className="flex gap-3">
                        <select className={`${inputClasses} w-28 font-bold text-center cursor-pointer`} value={formData.phone_code} onChange={e => setFormData({...formData, phone_code: e.target.value})}>
                          <option value="+90">+90</option>
                        </select>
                        <input type="tel" className={inputClasses} value={formData.phone} onChange={e => {
                          const val = e.target.value.replace(/\D/g,'');
                          setFormData({...formData, phone: val});
                          if(val.length === 10) document.getElementById('btn-next-1')?.focus();
                        }} placeholder="5XX XXX XX XX" maxLength={10} />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 2: İşletme Bilgileri */}
                {step === 2 && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-extrabold mb-6 text-gray-900 dark:text-white">İşletme Detayları</h3>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300">İşletme Adınız *</label>
                      <input type="text" className={inputClasses} value={formData.business_name} onChange={e => setFormData({...formData, business_name: e.target.value})} placeholder="Örn: Yılmaz Hair Studio" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 relative">
                      <div className="space-y-2 z-50">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">İl (Şehir) *</label>
                        <SearchableDropdown 
                          value={formData.city}
                          onChange={(val) => setFormData({...formData, city: val})}
                          placeholder="Şehir Seçiniz"
                        />
                      </div>
                      <div className="space-y-2 z-10">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">İlçe *</label>
                        <input type="text" className={inputClasses} value={formData.district} onChange={e => setFormData({...formData, district: e.target.value})} placeholder="Örn: Kadıköy" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300">İstediğiniz Alan Adı (Opsiyonel)</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">www.</span>
                        <input type="text" className={`${inputClasses} pl-14`} value={formData.requested_domain} onChange={e => setFormData({...formData, requested_domain: e.target.value})} placeholder="isletmeniz.com" />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3: Paket ve Onay */}
                {step === 3 && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-extrabold mb-6 text-gray-900 dark:text-white">İlgi ve İstekleriniz</h3>
                      
                      {/* Interest Level */}
                      <div className="mb-8">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block mb-3">Sistemle İlgilenme Düzeyiniz (1-5) *</label>
                        <div className="flex items-center gap-2">
                          {[1, 2, 3, 4, 5].map(rating => (
                            <button
                              key={rating}
                              onClick={() => setFormData({...formData, interest_level: rating})}
                              className="p-2 transition-transform hover:scale-110 focus:outline-none"
                            >
                              <Star 
                                size={32} 
                                className={`${formData.interest_level >= rating ? 'fill-amber-400 text-amber-400' : 'fill-transparent text-gray-300 dark:text-gray-600'} transition-colors`} 
                              />
                            </button>
                          ))}
                        </div>
                      </div>

                      {isLoading ? (
                        <div className="flex justify-center items-center h-32"><Loader2 className="animate-spin text-primary-500" size={32} /></div>
                      ) : (
                        <div className="space-y-3 mb-6">
                          <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Abonelik Paketi Seçin *</label>
                          <div className="grid grid-cols-1 gap-4">
                            {packages.map(pkg => (
                              <div 
                                key={pkg.id} 
                                onClick={() => setFormData({...formData, package_id: pkg.id})}
                                className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${formData.package_id === pkg.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md ring-4 ring-primary-500/10' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <div className={`font-bold text-lg mb-1 ${formData.package_id === pkg.id ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>{pkg.name}</div>
                                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{parseFloat(pkg.price).toLocaleString('tr-TR')} ₺ + KDV</div>
                                  </div>
                                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors ${formData.package_id === pkg.id ? 'border-primary-500 bg-primary-500 text-white' : 'border-gray-300 dark:border-gray-600'}`}>
                                    {formData.package_id === pkg.id && <Check size={16} className="stroke-[3]" />}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="space-y-2 mb-6">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Promosyon Kodu</label>
                        <div className="flex gap-3">
                          <div className="relative flex-1">
                            <Ticket size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input 
                              type="text" 
                              className={`${inputClasses} pl-12 uppercase font-bold tracking-wider`} 
                              value={promoInput} 
                              onChange={e => setPromoInput(e.target.value.toUpperCase())} 
                              placeholder="KUPONKODU"
                              disabled={appliedPromo !== null}
                            />
                          </div>
                          {appliedPromo ? (
                            <button onClick={() => {setAppliedPromo(null); setPromoInput(''); setFormData(p => ({...p, promo_code: ''}))}} className="px-6 py-3 bg-red-100 hover:bg-red-200 text-red-600 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400 rounded-xl font-bold transition-colors">İptal Et</button>
                          ) : (
                            <button onClick={validatePromo} disabled={!promoInput || validatingPromo} className="px-6 py-3 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 dark:text-gray-900 text-white rounded-xl font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] flex justify-center">
                              {validatingPromo ? <Loader2 className="animate-spin" size={24} /> : 'Uygula'}
                            </button>
                          )}
                        </div>
                        {promoError && <p className="text-red-500 text-sm mt-2 font-bold">{promoError}</p>}
                        {appliedPromo && <p className="text-green-600 dark:text-green-400 text-sm mt-2 font-bold flex items-center gap-1"><CheckCircle2 size={16}/> Kupon onaylandı: Net %{appliedPromo.discount_percentage} İndirim!</p>}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Ek Notlar (Opsiyonel)</label>
                        <textarea className={`${inputClasses} min-h-[120px] resize-none`} value={formData.extra_notes} onChange={e => setFormData({...formData, extra_notes: e.target.value})} placeholder="Sistemi fuarda beğendik, acil dönüş bekliyoruz..." />
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12 pt-8 border-t-2 border-gray-100 dark:border-gray-800">
              {step > 1 ? (
                <button onClick={handleBack} className="flex items-center gap-2 px-6 py-3.5 rounded-full text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <ChevronLeft size={20} /> Geri
                </button>
              ) : <div></div>}

              {step < 3 ? (
                <button id={`btn-next-${step}`} onClick={handleNext} className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-600 hover:bg-primary-500 text-white font-bold transition-all shadow-lg hover:-translate-y-1">
                  İleri <ChevronRight size={20} />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2 px-10 py-3.5 rounded-full bg-green-500 hover:bg-green-400 text-white font-extrabold transition-all shadow-xl shadow-green-500/40 hover:-translate-y-1 disabled:opacity-70 disabled:hover:translate-y-0">
                  {isSubmitting ? <><Loader2 className="animate-spin" size={20} /> İşleniyor...</> : 'Başvuruyu Tamamla'}
                </button>
              )}
            </div>
          </div>

          {/* Summary Panel Side */}
          <div className="w-full md:w-[40%] mt-8 md:mt-0 relative">
            <div className="sticky top-28 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8 rounded-[2rem] border-2 border-gray-200 dark:border-gray-800 flex flex-col">
              <h4 className="text-xl font-extrabold mb-6 flex items-center gap-3 text-gray-900 dark:text-white">
                <Scissors className="text-primary-500" size={24} /> Başvuru Özeti
              </h4>
              
              {!selectedPackage ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 dark:text-gray-500 py-10">
                  <div className="w-20 h-20 border-4 border-dashed border-gray-300 dark:border-gray-700 rounded-full flex items-center justify-center mb-6 opacity-50">?</div>
                  <p className="font-medium px-4">Paket seçimi yaptıktan sonra fiyat detayları burada belirecektir.</p>
                </div>
              ) : (
                <div className="flex flex-col h-full">
                  <div className="space-y-5 flex-1">
                    <div className="flex justify-between items-start pb-5 border-b border-gray-200 dark:border-gray-700">
                      <div>
                        <div className="font-bold text-gray-900 dark:text-white text-lg">{selectedPackage.name}</div>
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Abonelik Paketi</div>
                      </div>
                      <div className="font-bold text-lg text-gray-900 dark:text-white">{basePrice.toLocaleString('tr-TR')} ₺</div>
                    </div>
                    
                    {appliedPromo && (
                      <div className="flex justify-between items-center text-green-600 dark:text-green-400 pb-5 border-b border-gray-200 dark:border-gray-700 font-bold">
                        <div className="flex items-center gap-2"><Ticket size={18} /> Kupon ({appliedPromo.discount_percentage}%)</div>
                        <div>- {discountAmount.toLocaleString('tr-TR')} ₺</div>
                      </div>
                    )}

                    <div className="flex justify-between items-center text-sm font-semibold text-gray-500 dark:text-gray-400 pt-2">
                      <div>Ara Toplam</div>
                      <div>{netPrice.toLocaleString('tr-TR')} ₺</div>
                    </div>
                    <div className="flex justify-between items-center text-sm font-semibold text-gray-500 dark:text-gray-400">
                      <div>KDV ({taxRate}%)</div>
                      <div>{taxAmount.toLocaleString('tr-TR')} ₺</div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t-2 border-gray-200 dark:border-gray-700 bg-gray-100/50 dark:bg-gray-800/50 -mx-6 -mb-6 p-6 md:p-8 rounded-b-[2rem]">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider text-sm">Toplam Ödenecek</div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div className="text-4xl font-black text-primary-600 dark:text-primary-400 tracking-tight">{finalPrice.toLocaleString('tr-TR')} ₺</div>
                    </div>
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mt-3">* Fatura kurulum tamamlandıktan sonra kesilecektir.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default HairfestApplication;
